import React from 'react';
import { Button, Carousel } from 'react-bootstrap';
import { PencilSquare } from 'react-bootstrap-icons';
import moment from 'moment';
import { activityLocationParts } from './infoScreen2.utils';

function AnalogClock({ currentTime, sizeClassName = '' }) {
	const hour = moment(currentTime).hour();
	const minute = moment(currentTime).minute();
	const second = moment(currentTime).second();
	return (
		<div className="clock-container">
			<div className={`clock ${sizeClassName}`.trim()}>
				<div className="clock-face">
					<div
						className="hand hour-hand"
						style={{ transform: `rotate(${(hour % 12) * 30 + minute * 0.5}deg)` }}
					/>
					<div
						className="hand minute-hand"
						style={{ transform: `rotate(${minute * 6}deg)` }}
					/>
					<div
						className="hand second-hand"
						style={{ transform: `rotate(${second * 6}deg)` }}
					/>
					<div className="center-dot" />
				</div>
			</div>
		</div>
	);
}

function BilingualStack({ en, cn }) {
	if (!en && !cn) return null;
	return (
		<span className="info-screen-2-bilingual">
			{en ? <span className="info-screen-2-en">{en}</span> : null}
			{cn ? <span className="info-screen-2-cn">{cn}</span> : null}
		</span>
	);
}

function MealSection({ barLabel, en, cn }) {
	return (
		<div className="info-screen-2-meal-section">
			<div className="info-screen-2-meal-bar">{barLabel}</div>
			<div className="info-screen-2-meal-body">
				{en || cn ? (
					<>
						{en ? <div className="info-screen-2-en">{en}</div> : null}
						{en && cn ? <div className="info-screen-2-meal-lang-divider" /> : null}
						{cn ? <div className="info-screen-2-cn">{cn}</div> : null}
					</>
				) : (
					<div className="info-screen-2-empty">&nbsp;</div>
				)}
			</div>
		</div>
	);
}

const InfoScreen2Layout = ({
	isFullScreen,
	canViewAppointmentCalendar,
	canEditInfoScreen,
	medicalEvents,
	appointmentEventsCount,
	appointmentEmptyMessage = 'No medical appointments scheduled for today',
	appointmentTitleSuffix = '',
	appointmentPage,
	appointmentTotalPages,
	mealSections,
	activityPageEvents,
	activityEventsCount,
	activityEmptyMessage = 'No activities scheduled for today',
	noticeText,
	currentTime,
	weatherPayload,
	carouselImages,
	onEditNotice,
	onEditGallery,
}) => {
	const appointments = canViewAppointmentCalendar ? (medicalEvents || []) : [];
	const activityRows = activityPageEvents || [];
	const layout = (
		<div className="info-screen-2-layout">
			<div className="info-screen-2-panel info-screen-2-appt">
				<div className="info-screen-2-panel-title">Appointment / 看诊{appointmentTitleSuffix}</div>
				<div className="info-screen-2-panel-body">
					{canViewAppointmentCalendar ? (
						<>
							<table className="info-screen-2-table">
								<thead>
									<tr>
										<th>Time</th>
										<th>Customer Name</th>
										<th>Driver</th>
									</tr>
								</thead>
								<tbody>
									{appointmentEventsCount > 0 ? (
										appointments.map((event) => (
											<tr key={event.id}>
												<td>{event.driverPickupTime24 || ''}</td>
												<td>{event.customer}</td>
												<td>{event.driverName}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="3" className="info-screen-2-empty">{appointmentEmptyMessage}</td>
										</tr>
									)}
								</tbody>
							</table>
							{appointmentEventsCount > 0 && (
								<div className="info-screen-2-pager">
									Page {appointmentPage} / {appointmentTotalPages}
								</div>
							)}
						</>
					) : (
						<div className="info-screen-2-empty">Appointments not available</div>
					)}
				</div>
			</div>

			<div className="info-screen-2-panel info-screen-2-meal">
				<div className="info-screen-2-panel-title">Meal / 餐食{appointmentTitleSuffix}</div>
				<div className="info-screen-2-panel-body info-screen-2-meal-stack">
					<MealSection
						barLabel="Breakfast / 早餐"
						en={mealSections?.breakfast?.en}
						cn={mealSections?.breakfast?.cn}
					/>
					<MealSection
						barLabel="Lunch / 午餐"
						en={mealSections?.lunch?.en}
						cn={mealSections?.lunch?.cn}
					/>
				</div>
			</div>

			<div className="info-screen-2-panel info-screen-2-act">
				<div className="info-screen-2-panel-title">Activities / 活动{appointmentTitleSuffix}</div>
				<div className="info-screen-2-panel-body">
					<table className="info-screen-2-table">
						<thead>
							<tr>
								<th>Time</th>
								<th>Activities</th>
								<th>Location</th>
							</tr>
						</thead>
						<tbody>
							{activityEventsCount > 0 ? (
								activityRows.map((event, index) => {
									const location = activityLocationParts(event);
									return (
										<tr key={event.id || `activity-${index}`}>
											<td>{event.startTime24 || event.startTime}</td>
											<td>
												<BilingualStack en={event.title} cn={event.title_cn} />
											</td>
											<td>
												<BilingualStack en={location.en} cn={location.cn} />
											</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td colSpan="3" className="info-screen-2-empty">{activityEmptyMessage}</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			<div className="info-screen-2-panel info-screen-2-notice">
				<div className="info-screen-2-panel-heading">
					<div className="info-screen-2-panel-title">Notice / 公告栏</div>
					{!isFullScreen && canEditInfoScreen && (
						<Button variant="link" className="p-0 info-screen-2-edit" onClick={onEditNotice}>
							<PencilSquare size={16} />
						</Button>
					)}
				</div>
				<div className="info-screen-2-panel-body">
					{noticeText ? (
						<div className="info-screen-2-notice-text">{noticeText}</div>
					) : (
						<div className="info-screen-2-empty">No notice</div>
					)}
				</div>
			</div>

			<div className="info-screen-2-clockwx">
				<div className="info-screen-2-panel info-screen-2-time">
					<div className="info-screen-2-clock-body">
						<div className="info-screen-2-weekday">{moment(currentTime).format('dddd')}</div>
						<div className="info-screen-2-date">{moment(currentTime).format('D MMM, YYYY')}</div>
						<div className="info-screen-2-digital-time">{moment(currentTime).format('HH:mm')}</div>
						<AnalogClock currentTime={currentTime} sizeClassName={isFullScreen ? '' : 'clock-sm'} />
					</div>
				</div>
				<div className="info-screen-2-panel info-screen-2-weather">
					<div className="info-screen-2-clock-body">
						<div className="info-screen-2-weekday">
							{weatherPayload?.weather?.locationLabel || (weatherPayload?.zipcode ? `ZIP ${weatherPayload.zipcode}` : 'Weather location not set')}
						</div>
						<div className="info-screen-2-date">{weatherPayload?.weather?.condition || '—'}</div>
						<div className="info-screen-2-digital-time">
							{weatherPayload?.weather?.temperatureC != null
								? `${Math.round(weatherPayload.weather.temperatureC)}°C`
								: '—'}
						</div>
						<div className="weather-icon">
							<i className={weatherPayload?.weather?.iconClass || 'fas fa-cloud'} />
						</div>
					</div>
				</div>
			</div>

			<div className="info-screen-2-panel info-screen-2-gallery">
				<div className="info-screen-2-panel-heading">
					<div className="info-screen-2-panel-title">Gallery / 相册</div>
					{!isFullScreen && canEditInfoScreen && (
						<Button variant="link" className="p-0 info-screen-2-edit" onClick={onEditGallery}>
							<PencilSquare size={16} />
						</Button>
					)}
				</div>
				<div className="info-screen-2-panel-body info-screen-2-gallery-body">
					{carouselImages && carouselImages.length > 0 ? (
						<Carousel
							interval={carouselImages.length > 1 ? 5000 : null}
							controls={false}
							indicators={carouselImages.length > 1}
							className="info-screen-2-carousel"
						>
							{carouselImages.map((image, index) => (
								<Carousel.Item key={`${image}-${index}`}>
									<img className="d-block w-100 info-screen-2-carousel-img" src={image} alt={`Gallery ${index + 1}`} />
								</Carousel.Item>
							))}
						</Carousel>
					) : (
						<div className="info-screen-2-gallery-placeholder">No images in gallery</div>
					)}
				</div>
			</div>
		</div>
	);

	if (isFullScreen) return layout;

	return (
		<div className="info-screen-2-preview">
			{layout}
		</div>
	);
};

export default InfoScreen2Layout;
